import { useEffect, useState } from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
  Link as RouterLink,
  matchPath,
} from "react-router-dom";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ScienceIcon from "@mui/icons-material/Science";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";

import DashboardIcon from "@mui/icons-material/Dashboard";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import InventoryIcon from "@mui/icons-material/Inventory";
import TuneIcon from "@mui/icons-material/Tune";
import StoreIcon from "@mui/icons-material/Store";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import ReceiptIcon from "@mui/icons-material/Receipt";
import GroupIcon from "@mui/icons-material/Group";
import StraightenIcon from "@mui/icons-material/Straighten";
import CategoryIcon from "@mui/icons-material/Category";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import BadgeIcon from "@mui/icons-material/Badge";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import InsightsIcon from "@mui/icons-material/Insights";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import EditNoteIcon from "@mui/icons-material/EditNote";
import LockClockIcon from "@mui/icons-material/LockClock";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SearchIcon from "@mui/icons-material/Search";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { useQuery } from "@tanstack/react-query";
import { branchesApi } from "@/api/endpoints";
import { MenuItem as MuiMenuItem, TextField, InputAdornment } from "@mui/material";

import { useAuth } from "@/store/auth";
import { useFeatureFlags } from "@/store/featureFlags";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";
import type { TaxRegime } from "@/store/auth";
import { BrandLogo } from "@/components/BrandMark";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { useTenantAppearance } from "@/shared/hooks/useTenantAppearance";

const SIDEBAR_OPEN = 272;
const SIDEBAR_RAIL = 72;
const SIDEBAR_STATE_KEY = "im_sidebar_state"; // "open" | "rail" | "hidden"

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
  /**
   * Feature flag key required to show this item. Items without a `feature` are part of
   * the MVP 1 baseline and always show (subject to `permission`). Items with a `feature`
   * are hidden until an admin enables the flag at `/admin/feature-flags`.
   */
  feature?: string;
  /**
   * Tax regime gate. Items tagged here only render when the current tenant's regime
   * matches — e.g. GST e-invoice + e-way bill are India-specific so they're hidden
   * for tenants on `Vat` or `None`. Independent of feature flags: a VAT tenant might
   * have the `integration.gst.einvoice` flag mistakenly enabled, but the regime gate
   * still hides the menu entry because the integration would never produce results.
   */
  regimeOnly?: TaxRegime;
}

interface NavGroup {
  /** Stable id used by collapse state — does not appear in the UI. */
  key: string;
  /** Section title displayed as a subtle uppercase header in the sidebar. */
  title: string;
  items: NavItem[];
}

/**
 * Sidebar navigation organised by functional area. Phase metadata (MVP 1 / MVP 2 / Final)
 * is intentionally NOT surfaced here — it lives on each item's `feature` flag and is
 * enforced via the database-backed feature flag service. The admin toggles flags at
 * `/admin/feature-flags`; items materialise inside their functional group when their
 * flag flips on.
 *
 * Always-on items omit `feature`. MVP 2 / Final items declare the flag they belong to.
 */
const NAV: NavGroup[] = [
  {
    key: "main",
    title: "",
    items: [
      { label: "Dashboard",        path: "/dashboard",                       icon: <DashboardIcon /> },
    ],
  },
  {
    key: "catalog",
    title: "Catalog",
    items: [
      { label: "Items",            path: "/catalog/items",                   icon: <LocalPharmacyIcon />, permission: "inventory.item.view" },
      { label: "Vendors",          path: "/catalog/vendors",                 icon: <StoreIcon />,         permission: "procurement.vendor.view" },
      { label: "Vendor Items",     path: "/catalog/vendor-items",            icon: <FactCheckIcon />,     permission: "procurement.vendoritem.view" },
      { label: "Customers",        path: "/catalog/customers",               icon: <GroupIcon />,         permission: "sales.customer.view" },
      { label: "Units of Measure", path: "/catalog/uoms",                    icon: <StraightenIcon />,    permission: "catalog.uom.view" },
      { label: "Item Categories",  path: "/catalog/item-categories",         icon: <CategoryIcon />,      permission: "catalog.category.view" },
      { label: "Item Forms",       path: "/catalog/item-forms",              icon: <VaccinesIcon />,      permission: "catalog.form.view" },
      { label: "Item Routes",      path: "/catalog/item-routes",             icon: <VaccinesIcon />,      permission: "catalog.route.view" },
      { label: "Generic Names",    path: "/catalog/generic-names",           icon: <ScienceIcon />,       permission: "catalog.generic.view" },
      { label: "Supply Types",     path: "/catalog/supply-types",            icon: <Inventory2Icon />,    permission: "procurement.supplytype.view" },
      { label: "LASA (confusables)", path: "/catalog/lasa",                  icon: <WarningAmberIcon />,  permission: "catalog.lasa.view" },
      { label: "Item Substitutes", path: "/catalog/item-substitutes",        icon: <SwapHorizIcon />,     permission: "sales.substitution.view" },
      { label: "Services",         path: "/catalog/services",                icon: <MiscellaneousServicesIcon />, permission: "sales.service.view" },
      { label: "Service Groups",   path: "/catalog/service-groups",          icon: <AccountTreeIcon />,   permission: "sales.servicegroup.view" },
      { label: "Service Charges",  path: "/catalog/service-charges",         icon: <PriceChangeIcon />,   permission: "sales.servicecharge.view" },
      { label: "Admission Types",  path: "/catalog/admission-types",         icon: <AssignmentIndIcon />, permission: "catalog.admissiontype.view" },
      { label: "Person Types",     path: "/catalog/person-types",            icon: <BadgeIcon />,         permission: "catalog.persontype.view" },
      { label: "Insurance Schemes", path: "/catalog/insurance-schemes",      icon: <HealthAndSafetyIcon />, permission: "catalog.insurancescheme.view" },
      { label: "Currencies",       path: "/catalog/currencies",              icon: <CurrencyExchangeIcon />, permission: "catalog.currency.view" },
    ],
  },
  {
    key: "inventory",
    title: "Inventory",
    items: [
      { label: "Stock",            path: "/inventory/stock",                 icon: <InventoryIcon />,      permission: "inventory.stock.view" },
      { label: "Item Bins",        path: "/inventory/item-bins",             icon: <Inventory2Icon />,     permission: "inventory.location.view" },
      { label: "Adjustments",      path: "/inventory/adjustments",           icon: <TuneIcon />,           permission: "inventory.adjustment.view" },
      { label: "Write-offs",       path: "/inventory/writeoffs",             icon: <DeleteSweepIcon />,    permission: "inventory.writeoff.view" },
      { label: "Internal transfers",path: "/inventory/internal-transfers",   icon: <CompareArrowsIcon />,  permission: "inventory.transfer.internal" },
      { label: "Stock takes",      path: "/inventory/stocktakes",            icon: <FactCheckIcon />,      permission: "inventory.stocktake.view" },
      { label: "Near expiry",      path: "/inventory/near-expiry",           icon: <EventBusyIcon />,      permission: "inventory.stock.view" },
      { label: "Predictive reorder", path: "/inventory/predictive-reorder",  icon: <InsightsIcon />,       permission: "inventory.stock.view",   feature: "inventory.predictive_reorder" },
      { label: "Narcotic register", path: "/inventory/narcotic-register",    icon: <HistoryEduIcon />,     permission: "clinical.narcotic.view", feature: "clinical.narcotic_register" },
    ],
  },
  {
    key: "procurement",
    title: "Procurement",
    items: [
      { label: "Purchase Orders",  path: "/procurement/purchase-orders",     icon: <LocalShippingIcon />,        permission: "procurement.po.view" },
      { label: "Reorder queue",    path: "/procurement/reorder-queue",       icon: <ShoppingCartCheckoutIcon />, permission: "procurement.po.create" },
      { label: "Goods Receipts",   path: "/procurement/grns",                icon: <MoveToInboxIcon />,          permission: "procurement.grn.view" },
      { label: "Purchase Invoices",path: "/procurement/purchase-invoices",   icon: <RequestQuoteIcon />,         permission: "procurement.pi.view" },
      { label: "Direct PI (bill entry)", path: "/procurement/purchase-invoices/direct", icon: <RequestQuoteIcon />, permission: "procurement.pi.create-direct", feature: "procurement.direct-pi" },
      { label: "Quotations",       path: "/procurement/quotations",          icon: <RequestQuoteIcon />,         permission: "procurement.quotation.view",   feature: "procurement.quotation" },
      { label: "Purchase Indents", path: "/procurement/purchase-requisitions", icon: <AssignmentIcon />, permission: "procurement.requisition.create", feature: "procurement.requisition" },
    ],
  },
  {
    key: "sales",
    title: "Sales",
    items: [
      { label: "OP Billing",       path: "/sales/invoices",                  icon: <ReceiptIcon />, permission: "sales.invoice.view" },
      { label: "Prescription queue", path: "/sales/prescription-queue",      icon: <SyncAltIcon />, permission: "sales.invoice.view", feature: "integration.healntrack" },
      { label: "IP Billing",       path: "/sales/ip-billing",                icon: <ReceiptIcon />, permission: "sales.invoice.view", feature: "sales.ip_billing" },
      // W21 — statutory. Sits under Sales rather than Inventory because it is a record of
      // supplies made to patients, not of stock moving: the narcotic register next door answers
      // the stock question and has never known a patient or a prescriber.
      { label: "Schedule H1 register", path: "/sales/schedule-register",      icon: <HistoryEduIcon />, permission: "sales.line.schedule" },
    ],
  },
  {
    key: "warehousing",
    title: "Warehousing",
    items: [
      { label: "Transfers",        path: "/warehouse/transfers",                   icon: <SwapHorizIcon />,  permission: "warehouse.transfer.view", feature: "transfer.inter_branch" },
      { label: "Transfer requisitions", path: "/warehouse/transfer-requisitions",  icon: <AssignmentIcon />, permission: "warehouse.transfer.view", feature: "transfer.requisition" },
    ],
  },
  {
    key: "logistics",
    title: "Logistics",
    items: [
      { label: "Shipments",        path: "/transport/shipments",             icon: <LocalShippingIcon />, permission: "transport.shipment.view",  feature: "transport.shipment" },
      { label: "Carriers",         path: "/transport/carriers",              icon: <LocalShippingIcon />, permission: "transport.carrier.manage", feature: "transport.shipment" },
      { label: "Vehicles",         path: "/transport/vehicles",              icon: <LocalShippingIcon />, permission: "transport.carrier.manage", feature: "transport.shipment" },
      { label: "Drivers",          path: "/transport/drivers",               icon: <PersonIcon />,        permission: "transport.carrier.manage", feature: "transport.shipment" },
    ],
  },
  {
    key: "pricing",
    title: "Pricing",
    items: [
      { label: "Price lists",      path: "/pricing/price-lists",             icon: <RequestQuoteIcon />, permission: "pricing.list.view", feature: "pricing.price_list" },
      // No feature gate: rate cards group service rates as well as item ones, and services
      // are not behind the price-list flag (W11).
      { label: "Rate cards",       path: "/pricing/tariffs",                 icon: <RequestQuoteIcon />, permission: "pricing.tariff.view" },
      // W18 — packages cover services and stock together, so no feature gate here either.
      { label: "Packages",         path: "/pricing/packages",                icon: <RequestQuoteIcon />, permission: "pricing.package.view" },
      // W20 — consultants sit with pricing because a share rule is a pricing arrangement, even
      // though the person is billed against from the sales screens.
      { label: "Consultants",      path: "/sales/consultants",               icon: <RequestQuoteIcon />, permission: "sales.consultant.view" },
    ],
  },
  {
    key: "accounting",
    title: "Accounting",
    items: [
      { label: "Journals",          path: "/accounting/journals/new",        icon: <EditNoteIcon />,   permission: "accounting.journal.create" },
      { label: "Chart of accounts", path: "/accounting/chart-of-accounts",   icon: <AssignmentIcon />, permission: "accounting.account.view" },
      { label: "Periods",           path: "/accounting/periods",             icon: <LockClockIcon />,  permission: "accounting.journal.view" },
    ],
  },
  {
    key: "hospital",
    title: "Hospital",
    items: [
      { label: "Ward Issue",       path: "/hospital/ward",                   icon: <LocalHospitalIcon />, permission: "hospital.ward.issue",  feature: "hospital.ward" },
      { label: "OT Consumption",   path: "/hospital/ot",                     icon: <LocalHospitalIcon />, permission: "hospital.ot.issue",    feature: "hospital.ward" },
      { label: "CSSD Trays",       path: "/hospital/cssd",                   icon: <LocalHospitalIcon />, permission: "hospital.cssd.manage", feature: "hospital.ward" },
      { label: "Surgical Kits",    path: "/hospital/surgical-kits",          icon: <LocalHospitalIcon />, permission: "hospital.surgicalkit.view", feature: "hospital.ward" },
    ],
  },
  {
    key: "integrations",
    title: "Integrations",
    items: [
      { label: "HealNTrack",       path: "/integration/healntrack",          icon: <SyncAltIcon />,       permission: "integration.healntrack.sync",       feature: "integration.healntrack" },
      { label: "GST e-invoices",   path: "/integration/einvoices",           icon: <ReceiptIcon />,       permission: "integration.gst.einvoice.generate", feature: "integration.gst.einvoice", regimeOnly: "Gst" },
      { label: "E-Way bills",      path: "/integration/ewaybills",           icon: <LocalShippingIcon />, permission: "integration.gst.einvoice.generate", feature: "integration.eway_bill",   regimeOnly: "Gst" },
    ],
  },
  {
    key: "diagnostics",
    title: "Diagnostics",
    items: [
      { label: "Departments",       path: "/diagnostics/departments",     icon: <StoreIcon />,         permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Test Categories",   path: "/diagnostics/categories",      icon: <CategoryIcon />,      permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Specimens",         path: "/diagnostics/specimens",       icon: <TuneIcon />,          permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Containers",        path: "/diagnostics/containers",      icon: <TuneIcon />,          permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Rejection Reasons", path: "/diagnostics/rejection-reasons", icon: <TuneIcon />,        permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Report Templates",  path: "/diagnostics/report-templates", icon: <HistoryEduIcon />,   permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Rooms",             path: "/diagnostics/rooms",           icon: <WarehouseIcon />,     permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Facilities",        path: "/diagnostics/facilities",      icon: <WarehouseIcon />,     permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Machines",          path: "/diagnostics/machines",        icon: <TuneIcon />,          permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Facility Load",     path: "/diagnostics/facility-load",   icon: <InsightsIcon />,      permission: "diagnostics.order.view",       feature: "diagnostics.lab" },
      { label: "Lab Tests",         path: "/diagnostics/tests",           icon: <VaccinesIcon />,      permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Test Profiles",     path: "/diagnostics/profiles",        icon: <CategoryIcon />,      permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Reflex Rules",      path: "/diagnostics/reflex-rules",    icon: <SyncAltIcon />,       permission: "diagnostics.catalog.view",     feature: "diagnostics.lab" },
      { label: "Orders",            path: "/diagnostics/orders",          icon: <AssignmentIcon />,    permission: "diagnostics.order.view",       feature: "diagnostics.lab" },
      { label: "HealNTrack queue",  path: "/diagnostics/order-queue",     icon: <SyncAltIcon />,       permission: "diagnostics.order.view",       feature: "integration.healntrack" },
      { label: "New Order",         path: "/diagnostics/orders/new",      icon: <EditNoteIcon />,      permission: "diagnostics.order.create",     feature: "diagnostics.lab" },
      { label: "Sample Collection", path: "/diagnostics/collect",         icon: <MoveToInboxIcon />,   permission: "diagnostics.sample.collect",   feature: "diagnostics.lab" },
      { label: "Samples",           path: "/diagnostics/samples",         icon: <VaccinesIcon />,      permission: "diagnostics.order.view",       feature: "diagnostics.lab" },
      { label: "Lab Workbench",     path: "/diagnostics/lab-workbench",   icon: <FactCheckIcon />,     permission: "diagnostics.result.enter",     feature: "diagnostics.lab" },
      { label: "Results",           path: "/diagnostics/results",         icon: <FactCheckIcon />,     permission: "diagnostics.order.view",       feature: "diagnostics.lab" },
      { label: "Critical Values",   path: "/diagnostics/critical-values", icon: <NotificationsIcon />, permission: "diagnostics.critical.view",     feature: "diagnostics.lab" },
      { label: "QC Runs",           path: "/diagnostics/qc",              icon: <InsightsIcon />,      permission: "diagnostics.qc.view",          feature: "diagnostics.lab" },
      { label: "Barcode label",     path: "/diagnostics/barcode",         icon: <HistoryEduIcon />,    permission: "diagnostics.sample.collect",   feature: "diagnostics.lab" },
      { label: "Modalities",        path: "/diagnostics/radiology/modalities", icon: <LocalHospitalIcon />, permission: "diagnostics.catalog.view",  feature: "diagnostics.radiology" },
      { label: "Procedures",        path: "/diagnostics/radiology/procedures", icon: <CategoryIcon />,      permission: "diagnostics.catalog.view",  feature: "diagnostics.radiology" },
      { label: "Radiology Scheduling", path: "/diagnostics/radiology/scheduling", icon: <EditNoteIcon />,   permission: "diagnostics.study.schedule", feature: "diagnostics.radiology" },
      { label: "Radiology Reporting",  path: "/diagnostics/radiology/reporting",  icon: <HistoryEduIcon />, permission: "diagnostics.report.draft",   feature: "diagnostics.radiology" },
      { label: "Radiology Worklist",path: "/diagnostics/radiology-worklist", icon: <AssignmentIcon />, permission: "diagnostics.order.view",       feature: "diagnostics.radiology" },
      { label: "Radiology Safety",  path: "/diagnostics/radiology-safety", icon: <FactCheckIcon />,     permission: "diagnostics.safety.view",      feature: "radiology.safety" },
      { label: "PACS / Imaging",    path: "/diagnostics/pacs",            icon: <LocalHospitalIcon />, permission: "diagnostics.pacs.view",        feature: "diagnostics.pacs" },
      { label: "Critical Findings", path: "/diagnostics/critical-findings", icon: <NotificationsIcon />, permission: "diagnostics.finding.view",  feature: "diagnostics.radiology" },
      { label: "Dispatch",          path: "/diagnostics/dispatch",        icon: <LocalShippingIcon />, permission: "diagnostics.delivery.dispatch", feature: "diagnostics.lab" },
      { label: "Lab Dashboard",     path: "/diagnostics/lab-dashboard",   icon: <InsightsIcon />,      permission: "diagnostics.analytics.view",   feature: "diagnostics.lab" },
      { label: "Radiology Dashboard", path: "/diagnostics/radiology-dashboard", icon: <InsightsIcon />, permission: "diagnostics.analytics.view", feature: "diagnostics.radiology" },
      { label: "Reports & Analytics", path: "/diagnostics/analytics",      icon: <AssessmentIcon />,    permission: "diagnostics.analytics.view",   feature: "diagnostics.lab" },
      { label: "Lab TAT",           path: "/diagnostics/tat",             icon: <AssessmentIcon />,    permission: "diagnostics.analytics.view",   feature: "diagnostics.lab" },
      { label: "Notifications",     path: "/diagnostics/notifications",   icon: <NotificationsIcon />, permission: "diagnostics.notification.view", feature: "diagnostics.notifications" },
      { label: "Microbiology",      path: "/diagnostics/microbiology",    icon: <VaccinesIcon />,      permission: "diagnostics.microbiology.view", feature: "diagnostics.microbiology" },
      { label: "Histopathology",    path: "/diagnostics/histopathology",  icon: <FactCheckIcon />,     permission: "diagnostics.histopath.view",     feature: "diagnostics.histopath" },
      { label: "Outsourced Tests",  path: "/diagnostics/outsourced",      icon: <LocalShippingIcon />, permission: "diagnostics.outsourced.view",     feature: "diagnostics.outsourced" },
    ],
  },
  {
    key: "reports",
    title: "Reports",
    items: [
      { label: "Reports hub",      path: "/reports",                         icon: <AssessmentIcon /> ,  permission: "reports.stock.view" },
      // W23 — the department view a flat group list could never produce.
      { label: "Service revenue",  path: "/reports/service-revenue",         icon: <AssessmentIcon />,   permission: "reports.financial.view" },
      { label: "Audit log",        path: "/admin/audit",                     icon: <HistoryEduIcon />,   permission: "audit.log.view" },
    ],
  },
  {
    key: "admin",
    title: "Administration",
    items: [
      { label: "Users",            path: "/admin/users",                     icon: <PersonIcon />,       permission: "admin.user.view" },
      { label: "Roles",            path: "/admin/roles",                     icon: <SecurityIcon />,     permission: "admin.role.view" },
      { label: "Permissions",      path: "/admin/permissions",               icon: <SecurityIcon />,     permission: "admin.role.manage" },
      { label: "Branches",         path: "/admin/branches",                  icon: <StoreIcon />,        permission: "admin.branch.view" },
      { label: "Warehouses",       path: "/admin/warehouses",                icon: <WarehouseIcon />,    permission: "warehouse.warehouse.view" },
      { label: "Warehouse Locations", path: "/admin/warehouse-locations",    icon: <WarehouseIcon />,    permission: "inventory.location.view" },
      { label: "Feature flags",    path: "/admin/feature-flags",             icon: <TuneIcon />,         permission: "system.feature_flag.manage" },
      { label: "Tenant settings",  path: "/admin/tenant-settings",           icon: <TuneIcon />,         permission: "system.feature_flag.manage" },
      { label: "Appearance",       path: "/admin/appearance",                icon: <PaletteOutlinedIcon />, permission: "system.feature_flag.manage" },
      { label: "Demo guide",       path: "/demo-guide",                      icon: <SlideshowIcon />,    feature: "demo.guide" },
    ],
  },
];

function readSidebarState(): "open" | "rail" | "hidden" {
  const v = typeof window === "undefined" ? null : window.localStorage.getItem(SIDEBAR_STATE_KEY);
  return v === "rail" || v === "hidden" ? v : "open";
}

export function Layout() {
  const [sidebarState, setSidebarState] = useState<"open" | "rail" | "hidden">(readSidebarState());
  // Every titled section starts collapsed; the anonymous "main" group (Dashboard)
  // has no header toggle so it always stays visible.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(NAV.filter((g) => g.title.length > 0).map((g) => g.key)),
  );
  const [menuSearch, setMenuSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuth((s) => s.user);
  const activeBranchId = useAuth((s) => s.activeBranchId);
  const setActiveBranch = useAuth((s) => s.setActiveBranch);
  const hasPermission = useAuth((s) => s.hasPermission);
  const clear = useAuth((s) => s.clear);
  // Shell chrome follows the active theme, not just the mode — see theme/index.ts.
  const shell = useTheme().shell;
  const featureFlags = useFeatureFlags();
  const { regime } = useTaxRegime();
  // Pulls the tenant's house palette into the theme store. Lives here rather than in ThemedApp
  // because Layout only renders behind AuthGuard, and /tenants/current 401s when logged out.
  useTenantAppearance();

  // S7.5 multi-branch switcher: list of branches the user can see.
  const branches = useQuery({
    queryKey: ["branches-switcher"],
    queryFn: () => branchesApi.list(),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_STATE_KEY, sidebarState);
  }, [sidebarState]);

  const isHome = location.pathname === "/" || location.pathname === "/dashboard";

  const handleLogout = () => {
    clear();
    navigate("/login", { replace: true });
  };

  const cycleSidebar = () => {
    setSidebarState((s) => (s === "open" ? "rail" : s === "rail" ? "hidden" : "open"));
  };

  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const drawerWidth = sidebarState === "open" ? SIDEBAR_OPEN : sidebarState === "rail" ? SIDEBAR_RAIL : 0;
  const showLabels = sidebarState === "open";

  const query = menuSearch.trim().toLowerCase();

  const visibleItemsFor = (group: NavGroup) =>
    group.items.filter((i) =>
      (!i.permission || hasPermission(i.permission)) &&
      (!i.feature    || featureFlags.isEnabled(i.feature)) &&
      (!i.regimeOnly || i.regimeOnly === regime) &&
      (!query        || i.label.toLowerCase().includes(query)));

  const renderGroup = (group: NavGroup) => {
    const visibleItems = visibleItemsFor(group);
    if (visibleItems.length === 0) return null;
    // While searching, force every matching group open so results are always visible.
    const collapsed = query ? false : collapsedGroups.has(group.key);
    const hasTitle  = group.title.length > 0;

    return (
      <Box key={group.key} sx={{ mt: hasTitle ? 1 : 0 }}>
        {showLabels && hasTitle && (
          <ListItemButton onClick={() => toggleGroup(group.key)} sx={{ py: 0.5 }}>
            <ListItemText
              primaryTypographyProps={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                sx: { opacity: 0.65 },
              }}
              primary={group.title}
            />
            {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </ListItemButton>
        )}
        <Collapse in={!collapsed || !showLabels} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {visibleItems.map((item) => {
              const selected = !!matchPath({ path: item.path, end: false }, location.pathname);
              return (
                <ListItem key={item.path} disablePadding>
                  <Tooltip title={showLabels ? "" : item.label} placement="right">
                    <ListItemButton
                      component={RouterLink}
                      to={item.path}
                      selected={selected}
                      sx={{ minHeight: 48, justifyContent: showLabels ? "initial" : "center", px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: showLabels ? 2 : "auto", justifyContent: "center" }}>
                        {item.icon}
                      </ListItemIcon>
                      {showLabels && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ fontSize: 14 }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
        {showLabels && hasTitle && <Divider sx={{ my: 0.5 }} />}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* TOPBAR */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ minHeight: 64, gap: 1 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={cycleSidebar}
            aria-label="Toggle sidebar"
            sx={{ minWidth: 48, minHeight: 48 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Back + Home — always visible, mockup v12 rule */}
          <Tooltip title="Back">
            <span>
              <IconButton
                color="inherit"
                disabled={isHome}
                onClick={() => navigate(-1)}
                aria-label="Go back"
                sx={{ minWidth: 48, minHeight: 48 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Home">
            <IconButton
              color="inherit"
              onClick={() => navigate("/dashboard")}
              aria-label="Go to home"
              sx={{ minWidth: 48, minHeight: 48 }}
            >
              <HomeIcon />
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1.25 }}>
            <BrandLogo />
          </Box>

          {(branches.data?.length ?? 0) > 1 && (
            <TextField
              select size="small" variant="outlined"
              value={activeBranchId ?? ""}
              onChange={(e) => setActiveBranch(e.target.value || null)}
              sx={{ minWidth: 180 }}
            >
              {branches.data?.map(b => (
                <MuiMenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MuiMenuItem>
              ))}
            </TextField>
          )}

          <AppearanceMenu />

          <Tooltip title="Alerts (coming in S5)">
            <IconButton color="inherit" sx={{ minWidth: 48, minHeight: 48 }}>
              <Badge badgeContent={0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {user && (
            <>
              <Tooltip title={`${user.fullName} · ${user.roleName}`}>
                <IconButton
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  color="inherit"
                  sx={{ minWidth: 48, minHeight: 48 }}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "secondary.main" }}>
                    {user.fullName?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                <MenuItem disabled>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{user.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {user.roleName}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Sign out
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* SIDEBAR */}
      <Drawer
        variant="persistent"
        open={sidebarState !== "hidden"}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            bgcolor: shell.bg,
            color: shell.text,
            borderRight: "1px solid",
            borderColor: shell.border,
            "& .MuiListItemIcon-root": { color: shell.text },
            // Active nav item is blue-tinted on the navy sidebar.
            "& .MuiListItemButton-root.Mui-selected": {
              bgcolor: shell.activeBg,
              color: shell.activeText,
              "& .MuiListItemIcon-root": { color: shell.activeText },
              "&:hover": { bgcolor: shell.activeBg },
            },
            "& .MuiListItemButton-root:hover": { bgcolor: shell.hover },
            "& .MuiDivider-root": { borderColor: shell.border },
          },
          transition: "width 0.2s",
        }}
      >
        <Toolbar />
        {showLabels && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search menu…"
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              // The field sits on the dark navy shell, which stays dark in both modes. Left alone,
              // the typed text takes `text.primary` — near-black in light mode, so it vanished
              // against the sidebar (it only ever showed in dark mode by luck). Everything here is
              // pinned to shell tokens so the input matches the rest of the sidebar chrome.
              sx={{
                "& .MuiInputBase-root": { color: shell.brand },
                "& .MuiInputBase-input": {
                  color: shell.brand,
                  caretColor: shell.brand,
                  "&::placeholder": { color: shell.text, opacity: 1 },
                },
                "& .MuiSvgIcon-root": { color: shell.text },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: shell.border },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: shell.text },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: shell.activeText },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: menuSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear menu search"
                      onClick={() => setMenuSearch("")}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        )}
        <Box sx={{ overflow: "auto", flexGrow: 1, pb: 2 }}>
          {NAV.map(renderGroup)}
          {query && NAV.every((g) => visibleItemsFor(g).length === 0) && (
            <Typography
              variant="body2"
              sx={{ px: 2, py: 2, opacity: 0.6 }}
            >
              No menu items match “{menuSearch}”.
            </Typography>
          )}
        </Box>
        {showLabels && (
          <Box sx={{ p: 1, borderTop: "1px solid", borderColor: shell.border }}>
            <ListItemButton onClick={() => setSidebarState("rail")}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ChevronLeftIcon />
              </ListItemIcon>
              <ListItemText primary="Collapse" primaryTypographyProps={{ fontSize: 13 }} />
            </ListItemButton>
          </Box>
        )}
      </Drawer>

      {/* CONTENT */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: `calc(100% - ${drawerWidth}px)`,
          // A flex item defaults to min-width:auto, so a wide page (a fixed-width table, say)
          // stretches this box past the viewport and the whole app scrolls sideways. Pinning it
          // to 0 keeps the content column inside its track; wide content scrolls in its own box.
          minWidth: 0,
          transition: "width 0.2s",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
